import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getKmEntityTitle,
  type KmEntity,
  KM_NAME_ATTR_CODE,
  useKmEntityTypeAttrs,
  useUpdateKmEntity,
} from '@/entities/ontology';
import { EditableTitle } from '@/shared/ui';

type Props = {
  entity: KmEntity;
  /** Доступно ли изменение карточки (право на администрирование модели знаний) */
  canEdit: boolean;
};

/**
 * Название сущности в шапке карточки. Правка уходит своим запросом сразу по её завершении,
 * поэтому несохранённые правки остальных блоков карточки она не затрагивает и в её кнопку
 * сохранения не входит.
 */
export const KmEntityCardTitle: FC<Props> = ({ entity, canEdit }) => {
  const { t } = useTranslation('kmEntities');
  const { mutate: updateEntity } = useUpdateKmEntity();
  const { data: schemaAttrs } = useKmEntityTypeAttrs(entity.entityTypeCode);

  const name = typeof entity.data[KM_NAME_ATTR_CODE] === 'string' ? (entity.data[KM_NAME_ATTR_CODE] as string) : '';
  // Значение с кодом вне схемы типа бэкенд не примет, поэтому такое название только показывается
  const hasNameAttr = (schemaAttrs?.items ?? []).some(
    (attr) => attr.attrTypeCode === KM_NAME_ATTR_CODE && !attr.isDeleted,
  );

  const handleSave = (value: string) => {
    updateEntity({ id: entity.id, payload: { data: { [KM_NAME_ATTR_CODE]: value }, mode: 'merge' } });
  };

  return (
    <EditableTitle
      value={name}
      label={t('renameEntity')}
      placeholder={getKmEntityTitle(entity)}
      disabled={!canEdit || !hasNameAttr}
      onSave={handleSave}
      textClassName="text-xl font-semibold"
    />
  );
};
